import {
  EXPANDABLE_DETAILS_KEY,
  FIELD_DEFAULTS_GENERATOR_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaNestedShapeValues,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {getDefaultSeriesColorConfig, onChangeDiscreteColorPaletteMiddleware} from '../StepConfigUISchemas';

/**
 * Pie Chart configuration sections for the Configure tab
 */
export const PIE_CHART_CONFIG_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['nameSection', 'sliceSection', 'measureSection', 'colorPaletteSection'],
  nameSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['chartName'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    chartName: {
      displayName: 'Title',
      configShapePath: ['chartName'],
    },
  },
  sliceSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['sliceColumnName'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    sliceColumnName: {
      displayName: 'Slice',
      configShapePath: ['chartConfig', 'parameters', 'sliceColumnName'],
    },
  },
  measureSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['measureColumnName'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    measureColumnName: {
      displayName: 'Measure',
      configShapePath: ['chartConfig', 'parameters', 'measureColumnName'],
    },
  },
  colorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Color Palette',
    colorSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorConfigSection', 'seriesColorConfigsSection'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'colorConfigSection',
        detailFields: ['seriesColorConfigsSection'],
      },
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeDiscreteColorPaletteMiddleware,
      colorConfigSection: {
        displayName: 'Color palette',
        configShapePath: ['chartConfig', 'parameters', 'discreteColorPalette'],
        fieldSettings: {
          defaultValue: 'BENCHLING',
          displayNames: {
            BENCHLING: 'Benchling (30)',
            DARK24: 'Dark (24)',
            LIGHT24: 'Light (24)',
            BOLD: 'Bold (11)',
            PASTEL: 'Pastel (11)',
            SAFE: 'Safe (11)',
            PRISM: 'Crystal (11)',
          },
          svgImagePaths: {
            BENCHLING: '/static/img/insights/palettes/benchling.svg',
            DARK24: '/static/img/insights/palettes/dark24.svg',
            LIGHT24: '/static/img/insights/palettes/light24.svg',
            BOLD: '/static/img/insights/palettes/bold.svg',
            PASTEL: '/static/img/insights/palettes/pastel.svg',
            SAFE: '/static/img/insights/palettes/safe.svg',
            PRISM: '/static/img/insights/palettes/prism.svg',
          },
        },
      },
      seriesColorConfigsSection: {
        configShapePath: ['chartConfig', 'parameters', 'seriesColorConfigs'],
        editorOverride: StepConfigEditorOverride.MAPPING_ARRAY,
        fieldSettings: {
          mappingSourcePath: ['chartConfig', 'parameters', 'sliceColumnName'],
          mappingKey: 'series',
          showNoMappingEditor: true,
          noMappingDisplayName: 'Base color',
          noMappingSentinel: '__BENCHLING_NO_SERIES_DEFAULT__',
        },
        elementUISchema: {
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['series', 'color'],
          [FIELD_DEFAULTS_GENERATOR_KEY]: getDefaultSeriesColorConfig,
          series: {
            configShapePath: ['series'],
            disableFn: (): boolean => true,
            editorOverride: StepConfigEditorOverride.STRING_LABEL,
          },
          color: {
            configShapePath: ['color'],
            editorOverride: StepConfigEditorOverride.COLOR_PICKER,
          },
        },
      },
    },
  },
} as const satisfies ShapeUISchemaNestedShapeValues;

/**
 * Pie Chart style sections for the Style tab
 */
export const PIE_CHART_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['legendSection', 'sliceLabelSection'],
  legendSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Legend',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['showLegend', 'position', 'customLabel'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    showLegend: {
      configShapePath: ['chartConfig', 'parameters', 'showLegend'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show legend',
        defaultValue: true,
      },
    },
    position: {
      displayName: 'Position',
      configShapePath: ['chartConfig', 'parameters', 'legendLocation'],
      fieldSettings: {
        displayNames: {
          TOP: 'Top',
          LEFT: 'Left',
          RIGHT: 'Right',
        },
      },
    },
    customLabel: {
      displayName: 'Custom label (optional)',
      configShapePath: ['chartConfig', 'parameters', 'legendLabel'],
    },
  },
  sliceLabelSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Slice labels',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['showSliceNameInLabel', 'showPercentageInLabel', 'showValueInLabel'],
    showSliceNameInLabel: {
      configShapePath: ['chartConfig', 'parameters', 'showSliceNameInLabel'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show slice name',
      },
    },
    showPercentageInLabel: {
      configShapePath: ['chartConfig', 'parameters', 'showPercentageInLabel'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show percentage',
      },
    },
    showValueInLabel: {
      configShapePath: ['chartConfig', 'parameters', 'showValueInLabel'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show value',
      },
    },
  },
} as const satisfies ShapeUISchemaNestedShapeValues;
